import React, { useState } from 'react';
import { PlusIcon, Edit2Icon, Trash2Icon, SaveIcon, XIcon, ArrowUpIcon, ArrowDownIcon, PinIcon } from 'lucide-react';
import { useExperiments } from '../../lib/cmsStore';
import { ExperimentItem } from '../../lib/cmsTypes';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Select } from '../../components/ui/Select';
import { VersionHistoryPanel, VersionHistoryButton } from './components/VersionHistoryPanel';
export function ExperimentsEditor() {
  const {
    items,
    saveItem,
    deleteItem,
    reorderItems,
    isLoading
  } = useExperiments();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<ExperimentItem | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  if (isLoading) return <div className="p-8 font-mono text-sm" data-id="element-1217">Loading...</div>;
  // Sort items by order for display
  const sortedItems = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
  const handleEdit = (item: ExperimentItem) => {
    setEditForm({
      ...item
    });
    setEditingId(item.id);
  };
  const handleCreate = () => {
    const newItem: ExperimentItem = {
      id: Math.random().toString(36).substr(2, 9),
      title: 'New Experiment',
      shortDescription: '',
      longDescription: '',
      icon: 'ZapIcon',
      coverImage: '',
      previewMedia: '',
      type: 'Lab',
      tools: [],
      tags: [],
      liveDemoUrl: '',
      sourceUrl: '',
      featured: false,
      status: 'draft',
      slug: `new-experiment-${Date.now()}`,
      concept: '',
      process: '',
      result: '',
      gallery: [],
      notes: '',
      relatedIds: [],
      seoTitle: '',
      seoDescription: '',
      order: items.length,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setEditForm(newItem);
    setEditingId(newItem.id);
  };
  const handleSave = () => {
    if (editForm) {
      saveItem({
        ...editForm,
        updatedAt: new Date().toISOString()
      });
      setEditingId(null);
      setEditForm(null);
    }
  };
  const updateField = (field: keyof ExperimentItem, value: any) => {
    setEditForm(prev => prev ? {
      ...prev,
      [field]: value
    } : prev);
  };
  const togglePin = (item: ExperimentItem) => {
    saveItem({
      ...item,
      featured: !item.featured,
      updatedAt: new Date().toISOString()
    });
  };
  const moveItem = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0 || direction === 'down' && index === sortedItems.length - 1) {
      return;
    }
    const newItems = [...sortedItems];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    // Swap items
    const temp = newItems[index];
    newItems[index] = newItems[targetIndex];
    newItems[targetIndex] = temp;
    // Update order values
    const reorderedItems = newItems.map((item, i) => ({
      ...item,
      order: i
    }));
    reorderItems(reorderedItems);
  };
  return <div className="p-8 max-w-7xl mx-auto" data-id="element-1218">
      <div className="flex items-center justify-between mb-8" data-id="element-1219">
        <div data-id="element-1220">
          <h1 className="font-display text-3xl font-bold tracking-tight" data-id="element-1221">
            Experiments
          </h1>
          <p className="font-mono text-sm text-muted-foreground mt-2" data-id="element-1222">
            Manage your lab and creative explorations.
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-1223">
          <VersionHistoryButton onClick={() => setHistoryOpen(true)} data-id="element-1224" />
          {!editingId && <Button onClick={handleCreate} className="gap-2" data-id="element-1225">
              <PlusIcon className="w-4 h-4" data-id="element-1226" /> New Experiment
            </Button>}
        </div>
      </div>

      {editingId && editForm ? <div className="border border-border bg-card p-6 md:p-8 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-1227">
          <div className="flex items-center justify-between border-b border-border pb-4" data-id="element-1228">
            <h2 className="font-display text-2xl font-bold" data-id="element-1229">Edit Experiment</h2>
            <div className="flex gap-2" data-id="element-1230">
              <Button variant="ghost" onClick={() => setEditingId(null)} data-id="element-1231">
                <XIcon className="w-4 h-4 mr-2" data-id="element-1232" /> Cancel
              </Button>
              <Button onClick={handleSave} data-id="element-1233">
                <SaveIcon className="w-4 h-4 mr-2" data-id="element-1234" /> Save
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1235">
            <Input label="Title" value={editForm.title} onChange={e => updateField('title', e.target.value)} data-id="element-1236" />
            <Input label="Slug" value={editForm.slug} onChange={e => updateField('slug', e.target.value)} data-id="element-1237" />
            <Input label="Icon (Lucide name)" value={editForm.icon} onChange={e => updateField('icon', e.target.value)} data-id="element-1238" />
            <Input label="Type / Category" value={editForm.type} onChange={e => updateField('type', e.target.value)} data-id="element-1239" />
            <Select label="Status" value={editForm.status} onChange={e => updateField('status', e.target.value)} options={[{
          label: 'Draft',
          value: 'draft'
        }, {
          label: 'Published',
          value: 'published'
        }]} data-id="element-1240" />
            <div className="flex items-center h-10 mt-6" data-id="element-1241">
              <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-foreground cursor-pointer" data-id="element-1242">
                <input type="checkbox" checked={editForm.featured} onChange={e => updateField('featured', e.target.checked)} className="accent-primary w-4 h-4" data-id="element-1243" />
                Pin to top
              </label>
            </div>
          </div>

          <Textarea label="Short Description" value={editForm.shortDescription} onChange={e => updateField('shortDescription', e.target.value)} rows={2} data-id="element-1244" />

          <Textarea label="Long Description / Overview" value={editForm.longDescription} onChange={e => updateField('longDescription', e.target.value)} rows={4} data-id="element-1245" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1246">
            <Textarea label="Concept" value={editForm.concept} onChange={e => updateField('concept', e.target.value)} rows={4} data-id="element-1247" />
            <Textarea label="Process" value={editForm.process} onChange={e => updateField('process', e.target.value)} rows={4} data-id="element-1248" />
            <Textarea label="Result" value={editForm.result} onChange={e => updateField('result', e.target.value)} rows={4} data-id="element-1249" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1250">
            <Input label="Live Demo URL" value={editForm.liveDemoUrl} onChange={e => updateField('liveDemoUrl', e.target.value)} data-id="element-1251" />
            <Input label="Source Code URL" value={editForm.sourceUrl} onChange={e => updateField('sourceUrl', e.target.value)} data-id="element-1252" />
          </div>
        </div> : <div className="bg-card border border-border" data-id="element-1253">
          <div className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-4 p-4 border-b border-border font-mono text-xs uppercase tracking-widest text-muted-foreground" data-id="element-1254">
            <div className="w-16" data-id="element-1255">Order</div>
            <div data-id="element-1256">Experiment</div>
            <div className="w-24" data-id="element-1257">Type</div>
            <div className="w-24" data-id="element-1258">Status</div>
            <div className="w-12 text-center" data-id="element-1259">Pin</div>
            <div className="w-32 text-right" data-id="element-1260">Actions</div>
          </div>

          <div className="divide-y divide-border" data-id="element-1261">
            {sortedItems.length === 0 ? <div className="p-8 text-center text-muted-foreground font-mono text-sm" data-id="element-1262">
                No experiments found.
              </div> : sortedItems.map((item, index) => <div key={item.id} className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-4 p-4 items-center hover:bg-secondary/50 transition-colors" data-id="element-1263">
                  <div className="w-16 flex flex-col gap-1" data-id="element-1264">
                    <button onClick={() => moveItem(index, 'up')} disabled={index === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-1265">
                      <ArrowUpIcon className="w-4 h-4" data-id="element-1266" />
                    </button>
                    <button onClick={() => moveItem(index, 'down')} disabled={index === sortedItems.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-1267">
                      <ArrowDownIcon className="w-4 h-4" data-id="element-1268" />
                    </button>
                  </div>
                  <div data-id="element-1269">
                    <h3 className="font-display text-lg font-bold" data-id="element-1270">
                      {item.title}
                    </h3>
                    <p className="font-mono text-xs text-muted-foreground mt-1 truncate max-w-md" data-id="element-1271">
                      {item.shortDescription}
                    </p>
                  </div>
                  <div className="w-24 font-mono text-xs text-muted-foreground" data-id="element-1272">
                    {item.type}
                  </div>
                  <div className="w-24 flex flex-col gap-1" data-id="element-1273">
                    <span className={`inline-flex items-center justify-center px-2 py-1 font-mono text-[10px] uppercase tracking-widest border ${item.status === 'published' ? 'border-primary/50 text-primary bg-primary/10' : 'border-border text-muted-foreground bg-secondary'}`} data-id="element-1274">
                      {item.status}
                    </span>
                  </div>
                  <div className="w-12 flex justify-center" data-id="element-1275">
                    <button onClick={() => togglePin(item)} className={`p-1.5 rounded-md transition-colors ${item.featured ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:bg-secondary'}`} title={item.featured ? 'Unpin' : 'Pin to top'} data-id="element-1276">
                      <PinIcon className="w-4 h-4" data-id="element-1277" />
                    </button>
                  </div>
                  <div className="w-32 flex justify-end gap-2" data-id="element-1278">
                    <Button variant="ghost" size="sm" className="px-2" onClick={() => handleEdit(item)} title="Edit" data-id="element-1279">
                      <Edit2Icon className="w-4 h-4" data-id="element-1280" />
                    </Button>
                    <Button variant="ghost" size="sm" className="px-2 text-destructive hover:text-destructive" onClick={() => {
              if (window.confirm('Delete this experiment?')) deleteItem(item.id);
            }} title="Delete" data-id="element-1281">
                      <Trash2Icon className="w-4 h-4" data-id="element-1282" />
                    </Button>
                  </div>
                </div>)}
          </div>
        </div>}
      <VersionHistoryPanel contentKey="sinka_experiments" isOpen={historyOpen} onClose={() => setHistoryOpen(false)} onRestore={data => {
      if (Array.isArray(data)) reorderItems(data);
    }} data-id="element-1283" />
    </div>;
}